import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Collapse } from 'react-collapse';
import CSSModules from 'react-css-modules';
import { Icon, Badge, Tooltip, ChipOutlined } from 'platfi-ui-components';

import { bemModifier } from 'utils/BEMHelper';
import { ShareableReportLockSection } from 'modules/ShareableReport';

import styles from './ShareableReportSection.scss';

@CSSModules(styles, { allowMultiple: true })
class ShareableReportSection extends PureComponent {
  static propTypes = {
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    children: PropTypes.node.isRequired,
    column: PropTypes.string,
    customFooter: PropTypes.node,
    isHidden: PropTypes.bool,
    locked: PropTypes.bool,
    hideOnPrint: PropTypes.bool,
    smallSubtitleWidth: PropTypes.bool,
    toggleHidden: PropTypes.func,
    tooltipLabel: PropTypes.string,
    badgeTitle: PropTypes.string,
    badgeProps: PropTypes.shape(Badge.propTypes),
    alwaysVisible: PropTypes.bool,
  };

  static defaultProps = {
    column: 'o-col-sml-8 o-col-sml-offset-2',
    isHidden: false,
  };

  state = {
    isOpened: true,
  };

  componentDidMount() {
    this.props.isHidden && this.setState({ isOpened: false });
  }

  toggleCollapse = () =>
    !this.props.isHidden && this.setState((prevState) => ({ isOpened: !prevState.isOpened }));

  toggleHidden = (e) => {
    const { isHidden, toggleHidden } = this.props;

    e.stopPropagation();
    toggleHidden(!isHidden);
    this.setState({ isOpened: isHidden });
  };

  renderHeaderTip() {
    const { isHidden, tooltipLabel } = this.props;
    const defaultLabel = `${isHidden ? 'Show' : 'Hide'} section on shared report`;
    const icon = isHidden ? 'eye' : 'eye_private';
    const label = tooltipLabel || defaultLabel;

    return (
      <div
        styleName="m-shareable-report-section__hidden-flag"
        className="u-inline-block"
        onClick={this.toggleHidden}
        onKeyPress={this.toggleHidden}
        role="button"
        tabIndex={0}
      >
        <Tooltip
          primary
          small
          tooltipContent={label}
          top
          styleName="m-shareable-report-section__controls__eye-icon"
        >
          <Icon icon={icon} styleName="m-shareable-report-section__footer-content__icon" />
        </Tooltip>
      </div>
    );
  }

  renderRiskBadge() {
    const { badgeTitle, badgeProps } = this.props;
    return (
      <div styleName="m-shareable-report-section__risk-badge" className="u-hidden">
        <Badge {...badgeProps} label={badgeTitle} />
      </div>
    );
  }

  renderProLock = () => (
    <div className="u-inline-block" styleName="m-shareable-report-section__risk-badge">
      <ChipOutlined small grey square label="PRO" styleName="m-shareable-report-section__chip" />
      <Icon icon="lock" className="a-svg-large" styleName="m-shareable-report-section__lock-icon" />
    </div>
  );

  renderSectionHeader() {
    const { title, locked, isHidden, badgeTitle, alwaysVisible } = this.props;
    const arrowIcon = this.state.isOpened ? 'arrow_up' : 'arrow_down';
    const stylesName = classNames(
      ...bemModifier('m-shareable-report-section__header', {
        locked,
        disabled: isHidden,
      }),
    );

    const titleStylesName = classNames(
      ...bemModifier('m-shareable-report-section__title', { transparent: isHidden }),
    );

    const toggleStylesName = classNames(
      ...bemModifier('m-shareable-report-section__toggle', { hidden: isHidden }),
    );
    return (
      <div
        styleName={stylesName}
        className="o-container-d"
        onClick={this.toggleCollapse}
        onKeyPress={this.toggleCollapse}
        role="button"
        tabIndex={0}
      >
        <div styleName={titleStylesName} className="o-col-6">
          {title}
        </div>
        <div styleName="m-shareable-report-section__controls" className="o-col-6 u-align-right">
          {!alwaysVisible && !locked && this.renderHeaderTip()}
          {locked && this.renderProLock()}
          {badgeTitle && this.renderRiskBadge()}
          <div styleName={toggleStylesName}>
            <Icon icon={arrowIcon} />
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { locked, column, children, isHidden, subtitle, hideOnPrint, smallSubtitleWidth } =
      this.props;
    const styleNames = classNames({
      'm-shareable-report-section__print-disable': isHidden || hideOnPrint,
      'm-shareable-report-section': true,
    });

    return (
      <div className="o-container-d">
        <div styleName={styleNames} className={column}>
          {this.renderSectionHeader()}
          <Collapse isOpened={this.state.isOpened}>
            {locked ? (
              <ShareableReportLockSection
                subtitle={subtitle}
                smallSubtitleWidth={smallSubtitleWidth}
              />
            ) : (
              children
            )}
          </Collapse>
        </div>
      </div>
    );
  }
}

export default ShareableReportSection;
